import React, { useCallback, useRef } from 'react'
import { swapPositions } from '../utils/canvasSwap'
import type { SwapEntry } from '../utils/canvasSwap'
import { getCloseOriginPlan, getShovePlan, getSpreadPlan } from '../utils/canvasShove'
import type { SeamMark, ShoveAxis, ShoveTarget, SpreadOffer } from '../utils/canvasShove'
import { holdsZone, shoveTargetOf, swapZonesFor } from '../utils/canvasSwapZones'
import type { SwapZone } from '../utils/canvasSwapZones'
import { getAlignment } from '../utils/canvasAlignment'
import type { AlignmentLines, Bounds, MovingRect } from '../utils/canvasAlignment'
import { autoPanDelta } from '../utils/canvasAutoPan'
import { belongsToGroup, groupAround } from '../utils/canvasGroup'

/**
 * Die Klasse am `<html>`, solange getragen wird: Sie hält die geschlossene
 * Hand über dem ganzen Fenster (siehe `globals.scss`).
 *
 * Am `html` und nicht am Tauschpunkt, weil der Zeiger den 14px-Griff sofort
 * verlässt und danach über Notizen und Fläche steht, wo `CanvasFlow.scss` den
 * Pfeil setzt. Dasselbe Mittel wie beim Ziehen der Kante
 * (`canvas-resizing`).
 */
const SWAPPING_CLASS = 'canvas-swapping'

/**
 * Wie lange der Zeiger auf einem **Rand** ruhen muss, bis die Reihe ausweicht.
 *
 * Ruhen heißt nicht Stillstand, sondern: keine Reise mehr (siehe
 * `SPREAD_TRAVEL_DISTANCE`). Wer den Rand nur durchquert, um in den Kern zu
 * zielen, setzt damit keine Reihe in Bewegung; wer dort anhält, sieht die
 * Lücke sofort aufgehen. So kurz, dass ein Anhalten sich nicht wie Warten
 * anfühlt.
 *
 * **Der Tausch wartet nie.** Bis September 2026 bremste diese Ruhe ihn — die
 * Flächen lagen damals in der Lücke, und der Weg dorthin führte durch den
 * Tausch. Seit **Kern und Rand** ist es umgekehrt, und die Wartezeit liegt
 * dort, wo man ohnehin zielt und anhält.
 */
export const SPREAD_REST_DELAY = 60

/**
 * Wie weit der Zeiger in dieser Zeit kommen darf, ohne dass die Ruhe von vorn
 * beginnt — Bildschirm-px seit ihrem Beginn.
 *
 * Ohne dieses Maß begänne sie bei jeder Bewegung neu, und eine Hand, die den
 * Knopf hält und zielt, steht nie still: Jedes Zucken von wenigen Pixeln
 * schöbe das Ausweichen weiter hinaus. Gemessen wird deshalb die STRECKE seit
 * dem Beginn der Ruhe.
 */
export const SPREAD_TRAVEL_DISTANCE = 12

/** Was die Vorschau je Bewegung zeigt. */
export interface SwapPreview {
  /** Das gezogene Element und seine Lage am Zeiger — auch über einem **Leerplatz**: Eingerastet wird erst beim Loslassen. */
  dragged: { id: string; x: number; y: number }
  /**
   * Der Partner auf der getauschten Lage — der Zettel, in dessen **Kern** der
   * Zeiger steht; `null`, solange keiner getroffen ist. Der Tausch zeigt sich
   * sofort, ohne Wartezeit (CONTEXT.md, Platz tauschen).
   */
  target: { id: string; x: number; y: number } | null
  /** Die **Ableg-Flächen** des Zettels nahe dem Zeiger, und welche getroffen ist. */
  zones: SwapZone[]
  hit: SwapZone | null
  /**
   * Wer AUSWEICHT, solange der Zeiger auf einem **Rand** steht: die Seite,
   * auf die er zeigt, um die Breite genau dieses Randes (`getSpreadPlan`).
   * Leer, sobald kein Rand getroffen ist oder die Ruhe noch läuft — dann
   * stehen alle.
   */
  spread: { id: string; x: number; y: number }[]
  /**
   * Die Geste trägt FREI: keine Fläche getroffen, der Ring **verschiebt**
   * statt anzudocken (CONTEXT.md, Tauschpunkt). Dann rastet `dragged` schon
   * während des Ziehens ein und `lines` zeigt Hilfslinien und Maßklammern —
   * genau das Bild vom Zug am Zettelkörper.
   */
  free: boolean
  /** Hilfslinien und Maßklammern — nur im freien Zustand besetzt. */
  lines: AlignmentLines
  /**
   * Die **Zielfläche**: die Lage, auf der der Getragene beim Loslassen liegt —
   * beim Tausch der Platz des Partners, am **Leerplatz** das Feld.
   *
   * `null` beim **Einreihen** (dort erzählt die **Fuge** die Stelle, und die
   * Reihe weicht sichtbar aus) und im freien Zustand (dort rastet er schon
   * während des Ziehens selbst ein und STEHT auf seiner Zielposition; eine
   * Fläche darunter wäre sein eigener Schatten).
   */
  landing: MovingRect | null
  /**
   * **Wer ausweicht**: die Kennungen aller, die das Loslassen bewegt — sie
   * tragen den stillen **Umriss** (CONTEXT.md, Platz tauschen). Der Partner
   * steht nicht darin, er hat die atmende Stufe.
   *
   * Leer mit ⌘/Strg, im freien Zustand und daheim: Dort ist nur der Getragene
   * betroffen.
   */
  yielding: string[]
  /**
   * Die **Fuge**: die Marke des **Dazwischen schiebens**, mittig im Spalt, der
   * aufgeht. `null`, solange kein **Rand** getroffen ist, die Ruhe noch läuft
   * oder nichts aufgeht — sie gehört zum Angebot, nicht zum Treffer.
   */
  seam: SeamMark | null
}

/** Nichts eingerastet, nichts gezeigt. */
const NO_LINES: AlignmentLines = { vertical: [], horizontal: [], measures: [] }
/** Keine **Ableg-Fläche** — außerhalb der eigenen Gruppe gibt es keine. */
const NO_ZONES = { zones: [] as SwapZone[], hit: null }

interface UseSwapDragOptions {
  /**
   * Die Kandidaten samt Lage, zum Zeitpunkt des Anfassens — das gezogene
   * Element muss darunter sein. Wer sonst dazugehört, entscheidet der
   * Aufrufer (Auswahl oder Ausschnitt, siehe `CanvasFlow`).
   */
  getEntries: (dragged: string) => SwapEntry[]
  /** Bildschirm- in Canvas-Koordinaten (React Flows `screenToFlowPosition`). */
  toFlow: (position: { x: number; y: number }) => { x: number; y: number }
  /** Der Zoom — die Bänder der Ableg-Flächen sind bildschirmgroß, gerechnet wird in Canvas-Einheiten. */
  getZoom: () => number
  /** Einmal beim Anfassen — der Aufrufer friert daran seinen Rahmen ein. */
  onStart: (entries: SwapEntry[], dragged: string) => void
  /** Live, je Zeigerbewegung. */
  onPreview: (preview: SwapPreview) => void
  /** Beim Loslassen über einem Partner: hier wird festgeschrieben. */
  onCommit: (moved: { id: string; x: number; y: number }[]) => void
  /** Abbruch — Escape, `pointercancel`, oder ein Griff, der nie zur Geste wurde. */
  onCancel: () => void
  /**
   * Ob DIESE Geste allein unterwegs ist: **dazwischen schieben**,
   * **Leerplätze** UND freies **Verschieben** hängen daran. Die Gruppe darf
   * nichts davon — sie tauscht unter Mitgliedern (CONTEXT.md). Ohne Angabe:
   * nie.
   */
  canShove?: (dragged: string) => boolean
  /**
   * Das **Einrasten** für den freien Zustand — dieselben Ziele, dieselbe
   * Toleranz und dasselbe Punktraster wie beim Zug am Zettelkörper. Ohne
   * Angabe hängt der Zettel im Freien roh am Zeiger.
   *
   * Als Rückrufe und nicht als Werte: Der Ausschnitt kann sich während der
   * Geste verschieben (der Rand fährt mit), und sichtbar ist danach anderes.
   */
  align?: {
    /** Woran ausgerichtet wird — ohne den Getragenen. */
    getTargets: () => Bounds[]
    /** Die Fangweite in Canvas-Einheiten (bildschirmgroß gedacht, durch den Zoom geteilt). */
    threshold: () => number
    /** Das Punktraster, wenn nichts fluchtet. */
    grid: number
  }
  /**
   * **Der Ausschnitt fährt mit**, wenn der Zeiger an den Rand kommt — wie
   * beim Ziehen einer Notiz. Ohne Angabe fährt nichts.
   */
  autoPan?: {
    /** Ob DIESE Geste fahren darf (die Gruppe tauscht nur im Bild, sie fährt nicht). */
    enabled: (dragged: string) => boolean
    /** Der Canvas auf dem Bildschirm; `null`, wenn er nicht steht. */
    getBounds: () => { left: number; top: number; width: number; height: number } | null
    /** Den Ausschnitt um Bildschirm-Pixel schieben — React Flows `panBy`. */
    panBy: (delta: { x: number; y: number }) => Promise<boolean> | boolean
    /** Bildschirm-Pixel je Frame ganz außen — React Flows `autoPanSpeed`. */
    speed: () => number
  }
}

/**
 * **Platz tauschen**: die Zeiger-Geste am **Tauschpunkt**.
 *
 * **Zwei Zustände, eine Geste.** Solange eine Fläche getroffen ist, wird
 * ANGEDOCKT: Der Zettel hängt roh am Zeiger, der Partner springt, die Reihe
 * weicht aus, es gibt keine Hilfslinien — der Canvas ordnet selbst. Ist keine
 * Fläche mehr getroffen, wird VERSCHOBEN: Der Zettel rastet schon während des
 * Ziehens ein, Hilfslinien und Maßklammern stehen wie beim Zug am
 * Zettelkörper, und das Loslassen legt ihn dort ab, statt ihn
 * zurückspringen zu lassen. Dazwischen liegt kein Strich, sondern eine
 * Schwelle mit einer Handbreit Nachlass (`holdsZone`).
 *
 * Eigener Hook und nicht in `useGroupDrag` hinein: Dort geht es um den
 * **Auswahlrahmen** als Ganzes — Versatz, Einrasten, Ausrichtung der Gruppe.
 * Hier geht es um genau zwei Elemente und eine Regel, die mit alldem nichts
 * zu tun hat. Gemeinsam ist nur das Zeiger-Gerüst.
 *
 * **Die eingefrorene Ausgangslage ist der Kern.** Die Treffersuche läuft
 * IMMER gegen sie, nie gegen die laufende Vorschau: Man zielt auf einen
 * **Platz**, nicht auf ein wanderndes Element. Ohne das zerstörte sich die
 * Vorschau selbst — der Partner rutscht unter dem Zeiger weg, dort findet
 * sich nichts mehr, die Vorschau nimmt sich zurück, der Partner steht wieder
 * da, und das mehrmals pro Sekunde.
 *
 * **Die Listener hängen am `window`**, nicht am Tauschpunkt. Sonst stürbe die
 * Geste jedes Mal mit, wenn ihr Element verschwindet — durch Escape, durch
 * eine Löschung aus der Cloud-Sync, durch jeden Neuaufbau der Auswahl. Genau
 * dann bliebe das gezogene Element auf halbem Weg stehen, ohne je geschrieben
 * zu werden.
 *
 * @example
 * const { startSwap } = useSwapDrag({ … })
 * <SelectionOverlay onSwapStart={startSwap} />
 */
export function useSwapDrag({
  getEntries,
  toFlow,
  getZoom,
  onStart,
  onPreview,
  onCommit,
  onCancel,
  canShove,
  align,
  autoPan,
}: UseSwapDragOptions) {
  // Über ein Ref, damit der Handler stabil bleibt: Er hängt an den
  // Tauschpunkten, und deren Lage ändert sich während des Ziehens jeden Frame.
  const optionsRef = useRef({ getEntries, toFlow, getZoom, onStart, onPreview, onCommit, onCancel, canShove, align, autoPan })
  optionsRef.current = { getEntries, toFlow, getZoom, onStart, onPreview, onCommit, onCancel, canShove, align, autoPan }

  const startSwap = useCallback((id: string, event: React.PointerEvent) => {
    // Nicht an React Flow weiterreichen: Sonst startete der Node seinen
    // eigenen Ziehvorgang und die Greiffläche des Rahmens ihre Geste. Damit
    // ist zugleich der Klick ohne Ziehen erledigt — er kommt an der Notiz nie
    // an und ändert die Auswahl nicht.
    event.preventDefault()
    event.stopPropagation()

    const { getEntries, toFlow, onStart } = optionsRef.current
    // EINMAL eingefroren. Alles Weitere fragt diese Liste, nicht den
    // laufenden Zustand.
    const start = getEntries(id)
    const dragged = start.find(entry => entry.id === id)
    if (!dragged) return

    onStart(start, id)
    document.documentElement.classList.add(SWAPPING_CLASS)

    const origin = toFlow({ x: event.clientX, y: event.clientY })
    // Der Griffpunkt innerhalb des Elements. Ohne ihn spränge das Element mit
    // seiner Ecke an den Zeiger, sobald man es anfasst.
    const grip = { x: origin.x - dragged.rect.x, y: origin.y - dragged.rect.y }
    /*
      **Die Ableg-Flächen** (CONTEXT.md, Tauschpunkt): Je Bewegung rechnet
      `swapZonesFor` gegen die eingefrorene Lage, welche Fläche der Zeiger
      trifft — den **Kern** eines Zettels (tauschen) oder einen seiner
      **Ränder** (dazwischen schieben). Aus der Gruppe heraus gibt es keine
      Ränder (`canShove`): Sie tauscht unter Mitgliedern.

      **Der Tausch zeigt sich sofort**, ohne jede Wartezeit: Der Rand liegt
      VOR dem Kern, wer von außen kommt, kreuzt ihn zuerst — es gibt keinen
      Weg zum Schieben, der durch den Tausch führte. (Bis September 2026 lagen
      die Flächen in der LÜCKE hinter dem Zettel; dagegen brauchte es eine
      Richtungsmessung, die den Tausch bremste. Beides ist entfallen.)

      **Das Ausweichen wartet eine kurze Ruhe** (`SPREAD_REST_DELAY`): Wer den
      Rand nur durchquert, um in den Kern zu zielen, soll keine Reihe in
      Bewegung setzen. Neu gezählt wird erst, wenn der Zeiger seither
      `SPREAD_TRAVEL_DISTANCE` weit gereist ist — eine Hand am Knopf zittert,
      und jedes Zucken zu zählen hieße: nie ausweichen. Das Fahren des
      Ausschnitts bewegt den Zeiger nicht und zählt darum ohnehin nicht.
      Getroffen ist getroffen: Wer währenddessen loslässt, reiht ein, auch
      wenn sich noch nichts geöffnet hatte.
    */
    let hit: SwapZone | null = null
    /*
      **Die Schwelle mit Nachlass** (CONTEXT.md, Tauschpunkt): Was zuletzt
      getroffen war, hält den Zeiger noch eine Handbreit über seine Kante
      hinaus (`holdsZone`). Sonst kippte der Zustand an jeder Feldkante
      mehrmals pro Sekunde zwischen **Andocken** und **Verschieben** hin und
      her. Zurück ins Andocken geht es nur über einen echten Treffer.
    */
    let held: SwapZone | null = null
    let heldZones: SwapZone[] = []
    /** Wo der Zettel im freien Zustand LÄGE — eingerastet; `null` beim Andocken. */
    let landing: { x: number; y: number } | null = null
    /*
      Erst eine Bewegung macht aus dem Griff eine Geste. Ohne das setzte schon
      ein KLICK auf den Tauschpunkt den Zettel ab (die erste Vorschau läuft
      sofort, noch bevor sich der Zeiger rührt) — und ein Zettel, der nicht
      auf dem Punktraster liegt, spränge beim bloßen Anfassen darauf.
    */
    let travelled = false
    /*
      **Der eigene Platz ist ein Ziel** (CONTEXT.md, Tauschpunkt): der Platz,
      den der Getragene gerade verlässt — mit seiner **Zielfläche** wie jedes
      **Feld** des Rings, aber ohne Folgen. Loslassen schreibt dort nichts, der
      Zettel bleibt, wo er war, und niemand rückt (`finish`). Ohne ihn hatte
      der Ring ausgerechnet in seiner Mitte ein Loch: Überall sagte die Fläche,
      wohin der Zettel fällt, nur daheim sagte nichts, dass man ihn dort wieder
      hinlegen kann (gewünscht 2026-09-08).

      **Er hält die Geste am Andocken.** Beim Anfassen steht der Zeiger mitten
      auf dem Getragenen; ohne diese Fläche begänne JEDE Geste im freien
      Zustand, und die Hilfslinien blitzten auf, um beim ersten Feld wieder zu
      verschwinden.

      **Und er beendet die Nachsicht.** Eine einmal getroffene Fläche wird über
      kleine Lücken hinweg gehalten (`holdsZone`) — aber nicht bis auf den
      Platz, von dem der Zettel kommt: Dort gibt es ein besseres Angebot, und
      ein frischer Treffer löst den alten ab. Der Tauschpunkt sitzt auf der
      OBEREN Kante; steht der Nachbar darüber, liegt der Zeiger daheim nur
      einen **Takt** unter dessen Unterkante und damit noch in dessen
      Nachsicht. Der Tausch überlebte so den ganzen Rückweg und schlug beim
      Loslassen zu, obwohl der Zettel längst wieder daheim war (gemeldet
      2026-09-04).

      **Daheim gewinnt er gegen jedes FREMDE Feld.** Steht der Zettel nicht
      genau in der Flucht seines Nachbarn, liegt dessen **Leerplatz** über
      seinem Platz — versetzt um genau diesen Unterschied, und ein Feld, das
      den alten Platz nur ÜBERLAPPT, ist ein gültiges Angebot. Daheim war es
      damit schon unter dem Zeiger, bevor die Geste begann: Ein Zug von dreißig
      Pixeln ließ den Zettel um einen **Takt** springen (gemeldet 2026-09-04,
      Bild). Wer sein Feld will, verlässt dafür seinen Platz — es reicht ja
      weit genug hinaus. Kern und Rand bleiben unangetastet: Sie liegen auf
      fremdem Papier, und wer darauf zielt, meint es.
    */
    const ownPlace: SwapZone = { owner: dragged, kind: 'home', rect: dragged.rect }
    /*
      Allein unterwegs? Dann gibt es Ränder, Leerplätze und das freie
      Verschieben. Aus der Gruppe heraus nichts davon: Sie tauscht unter
      Mitgliedern, und ein Mitglied, das frei davonzöge, risse den Rahmen
      auseinander.
    */
    const solo = optionsRef.current.canShove?.(id) ?? false
    /*
      **Die Gruppe, aus der er kommt** (CONTEXT.md, Tauschpunkt): Angedockt
      wird nur, solange der Getragene noch zu ihr gehören WÜRDE — höchstens
      eine Zettellänge Luft. Weiter draußen VERSCHIEBT die Geste, mit
      Hilfslinien und Einrasten, als hätte man am Zettel selbst gezogen; dort
      gibt es weder Kern noch Rand noch **Leerplatz**, und der Ursprung bleibt
      ein Loch.

      **Einmal beim Anfassen gerechnet**, wie die Ausgangslage selbst: Die
      Gruppe ist eine Eigenschaft der Lage, und die ändert der Griff nicht.
    */
    const group = groupAround(start, id, dragged.rect)
    /*
      **⌘/Strg hält den Ursprung offen** — und wird LAUFEND gelesen, nicht erst
      beim Loslassen: Solange die Taste unten ist, ist nur der Getragene
      betroffen, und das Bild muss es sagen können (CONTEXT.md, Platz
      tauschen: „der Umriss trägt zwei Stufen"). Gezählt wird beim Loslassen
      trotzdem der Stand des `pointerup`-Ereignisses — die Vorschau ist nur
      das Bild.
    */
    let keepOrigin = event.metaKey || event.ctrlKey
    /** Welcher Rand die Ruhe schon abgesessen hat. */
    let settled: string | null = null
    let rest: ReturnType<typeof setTimeout> | null = null
    /* Wo der Zeiger stand, als die Ruhe begann — von hier misst die Reise. */
    let restFrom: { x: number; y: number } | null = null
    let lastPointer = origin
    const stopResting = () => {
      restFrom = null
      if (rest === null) return
      clearTimeout(rest)
      rest = null
    }
    /*
      Woran die Ruhe hängt: an dem, was das Angebot ZEIGT — nicht daran,
      welche Fläche getroffen ist.

      Zwischen zwei Nachbarn liegen ZWEI Flächen, die dasselbe bedeuten: der
      folgende Rand des einen und der führende des anderen. Hing die Ruhe an
      der Fläche, fiel der Spalt beim Überfahren der Grenze in sich zusammen
      und ging 60 ms später wieder auf — ein Wackeln mitten in der ruhigen
      Hand (gemeldet 2026-09-04). Am Ergebnis gemessen ist es dieselbe Naht,
      und nichts rührt sich.
    */
    const offerKeyOf = (target: ShoveTarget) => {
      const { rect } = getShovePlan(start, id, target, true)
      return `${target.axis}:${Math.round(rect.x)}:${Math.round(rect.y)}`
    }

    /**
     * Das **Angebot** am **Leerplatz**: Auch dort rückt jemand — die
     * Ursprungsreihe schließt sich (CONTEXT.md, Leerplätze), und wer ausweicht,
     * soll es schon vor dem Loslassen zeigen. Gerechnet wird derselbe Plan,
     * den auch das Loslassen ausführt.
     *
     * `null` mit ⌘/Strg: Dann rührt sich niemand, und es gibt nichts
     * anzukündigen.
     */
    const closingAt = (field: MovingRect) =>
      (keepOrigin ? null : getCloseOriginPlan(start, id, field))

    /** Die Weite des Spalts: die Fläche, auf der die Hand steht (CONTEXT.md, Angebot). */
    const openingOf = (zone: SwapZone, axis: ShoveAxis) =>
      axis === 'x' ? zone.rect.width : zone.rect.height

    const preview = (pointer: { x: number; y: number }) => {
      lastPointer = pointer
      const zoom = optionsRef.current.getZoom()
      /*
        Wo der Zettel LÄGE — daran hängt die Frage, ob er noch zur Gruppe
        gehört. Am Zettel gemessen, nicht am Zeiger: Der Zeiger sitzt am
        **Tauschpunkt** auf seiner oberen Kante, und gemeint ist das Papier.
      */
      const carried = { ...dragged.rect, x: pointer.x - grip.x, y: pointer.y - grip.y }
      const away = solo && travelled && !belongsToGroup(carried, group, dragged.rect)

      const home = !away && holdsZone(ownPlace, pointer, zoom)
      /*
        **Im Ring wird angedockt — immer.** In Reichweite der eigenen Gruppe
        gibt es keinen Fehlschlag: Trifft der Zeiger weder Kern noch Rand noch
        ein Feld, gilt das nächstgelegene Feld (`nearest`). Zwischen den
        Feldern und in ihren Ecken bleiben sonst Lücken, in denen die Geste
        mitten in der Gruppe in den freien Zustand fiel und aufs Punktraster
        rastete (gemeldet 2026-09-05: „das Raster der Gruppe trifft er nie").

        Daheim NICHT: Wer auf seinem eigenen Platz steht, zielt auf DIESEN —
        ein fremdes Feld unter ihm wäre kein Ziel, sondern ein Sprung
        (CONTEXT.md, Tauschpunkt).
      */
      const zones = away
        ? NO_ZONES
        : swapZonesFor(start, id, pointer, zoom, { bands: solo, nearest: home ? undefined : carried })
      const fresh = zones.hit && !(home && zones.hit.kind === 'slot') ? zones.hit : null
      if (fresh) {
        held = fresh
        heldZones = zones.zones
      } else if (home) {
        // Daheim gilt der eigene Platz — ein Treffer wie jeder andere, nur
        // folgenlos. Er löst damit auch die Nachsicht des vorigen ab.
        held = ownPlace
        heldZones = []
      } else if (away || !(held && holdsZone(held, pointer, zoom))) {
        // Draußen gilt kein Nachlass: Die Schwelle liegt eine Zettellänge weit
        // weg, dort wackelt keine Hand hin und her.
        held = null
        heldZones = []
      }
      hit = held
      const shove = hit ? shoveTargetOf(hit) : null

      /*
        Die Ruhe gilt dem Ausweichen, nicht dem Treffer — am **Rand** wie am
        **Leerplatz**: Wer eine Fläche nur durchquert, soll keine Reihe in
        Bewegung setzen.
      */
      const closing = hit?.kind === 'slot' ? closingAt(hit.rect) : null
      /*
        **Nur der Rand wartet.** Am **Leerplatz** ist die Ruhe entfallen: Was
        dort rückt, hängt gar nicht am getroffenen Feld — `closingOn` rechnet
        die Ursprungsreihe, nicht das Ziel. Über welchem Feld des Rings der
        Zeiger auch steht, es rücken dieselben Zettel gleich weit; es gibt
        also nichts, was beim Durchqueren flackern könnte. Und der Landeplatz
        hängt sehr wohl daran (siehe `settling`) — ihn zu verzögern hieß, ein
        Versprechen zu geben, das das Loslassen bricht.
      */
      const offerKey = shove ? offerKeyOf(shove) : null
      if (hit && offerKey) {
        const key = offerKey
        if (settled !== key) {
          const travelled = restFrom === null
            ? Infinity
            : Math.hypot(lastClient.x - restFrom.x, lastClient.y - restFrom.y)
          if (rest === null || travelled >= SPREAD_TRAVEL_DISTANCE) {
            stopResting()
            restFrom = { x: lastClient.x, y: lastClient.y }
            rest = setTimeout(() => {
              rest = null
              settled = key
              preview(lastPointer)
            }, SPREAD_REST_DELAY)
          }
        }
      } else {
        settled = null
        stopResting()
      }

      const partner = hit?.kind === 'on' ? hit.owner : null
      const swapped = partner ? swapPositions(dragged.rect, partner.rect) : null
      const spreading = hit !== null && offerKey !== null && settled === offerKey

      /*
        **Frei tragen** heißt: einrasten wie beim Zug am Zettelkörper — erst
        an fluchtenden Kanten (Hilfslinie), sonst am Punktraster. Beim
        **Andocken** rastet nichts: Dort ordnet der Canvas selbst, und der
        Zettel hängt roh am Zeiger, bis er ins Feld gelegt wird.

        **Auch über einem Leerplatz nicht.** Der Feld-Ring gehört zum
        Andocken, mitsamt seinen Feldern; eine Linie darin machte ein zweites
        Angebot, wo der Canvas schon eines gemacht hat. Die Linien beginnen
        erst JENSEITS des Rings — dort, wo niemand mehr etwas anbietet.
        (Kurz probiert am 2026-09-04, für die Lage des Feldes gerechnet; im
        Bild las es sich als Unruhe mitten in der Gruppe.)
      */
      const raw = { x: carried.x, y: carried.y }
      const free = hit === null && solo && travelled && !home
      const align = optionsRef.current.align
      let lines = NO_LINES
      let place = raw
      if (free && align) {
        const result = getAlignment(
          { ...raw, width: dragged.rect.width, height: dragged.rect.height },
          align.getTargets(),
          align.threshold(),
          align.grid,
        )
        place = result.position
        lines = { vertical: result.vertical, horizontal: result.horizontal, measures: result.measures }
      }
      landing = free ? place : null

      /*
        Die **Fuge** (CONTEXT.md, Dazwischen schieben): die Marke, die das
        Einreihen vom Tausch unterscheidet — dort ist es der **Umriss** um den
        Partner, und den zeichnet der Zettel selbst (`target`). Die Fuge kommt
        MIT dem Angebot, nicht vor ihm: Sie zeigt eine Öffnung, und solange
        die Ruhe läuft, ist keine da.
      */
      let offer: SpreadOffer | null = null
      if (shove && hit && spreading) {
        offer = getSpreadPlan(start, id, shove, openingOf(hit, shove.axis))
      }

      /*
        **Auch am Leerplatz rückt die Reihe schon** (CONTEXT.md, Leerplätze).
        Bis 2026-09-05 blieb sie stehen, „damit das Feld unter dem Zeiger nicht
        wegwandert, während die Marke steht" — die **Zielfläche** wandert jetzt
        mit, also steht der Einwand nicht mehr. Und ausgerechnet dort schließt
        sich der Ursprung: Wer nichts davon sah, sah beim Loslassen einen
        Zettel an einer Stelle landen, auf die er nicht gezeigt hatte
        (gemeldet 2026-09-05).

        Die volle Strecke, nicht ein Teilstück wie am **Rand**: Dort öffnet
        sich ein Spalt vor dem Zeiger, hier schließt sich eine Lücke hinter
        ihm — sie läuft ihm nicht davon.

        **Und ohne Ruhe, seit September 2026.** Bis dahin hing auch dies an
        `spreading`, und wer vor Ablauf der 60ms losließ — ein Schwung, kein
        Zögern —, sah den Zettel um einen Takt springen: Die Vorschau zeigte
        das Feld, das Loslassen rechnete die geschlossene Lücke mit
        (`getCloseOriginPlan`, unten in `finish`). Beides ist EINE Bewegung;
        sie darf nicht zur Hälfte warten. Gemeldet vom Nutzer, gefunden über
        `canvas-slot-grid` im E2E.
      */
      const settling = closing

      /*
        Die **Zielfläche** (CONTEXT.md, Tauschpunkt): wo er landet, in seinen
        eigenen Maßen. Über einem **Leerplatz** sagte bisher gar nichts, wohin
        der Zettel springt, und beim Tausch nur der **Umriss**, WER weicht.

        **Beim Einreihen nicht**: Dort steht die **Fuge** in der Lücke, und die
        Reihe weicht sichtbar aus — die Stelle ist schon erzählt. Eine Fläche
        obendrauf wäre die dritte Marke für denselben Satz.

        **Daheim schon**: Der eigene Platz ist ein Ziel wie jedes Feld, und die
        Fläche sagt dort dasselbe wie überall — hier landet er. Sie steht auf
        der Ausgangslage; es rückt ja niemand, der sie verschöbe.

        Gerechnet wird nichts Neues: Der Tausch steht in `swapped`, das Feld
        ist der Treffer selbst.
      */
      const size = { width: dragged.rect.width, height: dragged.rect.height }
      // `ziel` und nicht `landing`: Das ist oben schon vergeben — die Lage im
      // freien Zustand, die das Loslassen festschreibt.
      const ziel: MovingRect | null =
        swapped ? { ...swapped.a, ...size }
        : hit?.kind === 'slot' ? (settling ? settling.rect : hit.rect)
        : hit?.kind === 'home' ? hit.rect
        : null

      /*
        **Wer ausweicht, zeigt es** (CONTEXT.md, Platz tauschen): der stille
        **Umriss** an jedem, den das Loslassen bewegt. Der Partner steht nicht
        darin — er trägt die atmende Stufe. Mit ⌘/Strg ist die Liste leer:
        Dann ist nur der Getragene betroffen, und man sieht es.

        Gerechnet, nicht geraten: dieselben Pläne, die das Loslassen ausführt.
        Ohne Ruhe — dass jemand betroffen IST, gilt sofort; nur sein Rücken
        wartet.
      */
      const yielding = shove
        ? getShovePlan(start, id, shove, !keepOrigin).moves.map(move => move.id)
        : closing ? closing.moves.map(move => move.id)
        : []

      optionsRef.current.onPreview({
        dragged: { id, ...place },
        target: partner && swapped ? { id: partner.id, ...swapped.b } : null,
        zones: hit ? heldZones : [],
        hit,
        spread: offer ? offer.moves : settling ? settling.moves : [],
        free,
        lines,
        landing: ziel,
        yielding,
        seam: offer ? offer.seam : null,
      })
    }

    /*
      Der Ausschnitt fährt mit (siehe `autoPan` an den Optionen). Je Frame:
      Steht der Zeiger in der Randzone, den Ausschnitt schieben — und die
      Vorschau NEU rechnen, denn derselbe Bildschirmpunkt liegt danach
      woanders auf dem Canvas; das getragene Element muss nachziehen, ohne
      dass sich der Zeiger rührt. Die Treffersuche läuft weiter gegen die
      eingefrorene Ausgangslage in Canvas-Koordinaten, die ändert das Fahren
      nicht. Erst nach dem `await`: `panBy` schreibt den Store asynchron,
      und eine Vorschau davor rechnete mit dem alten Versatz.
    */
    const lastClient = { x: event.clientX, y: event.clientY }
    let frame: number | null = null
    let finished = false
    const panning = optionsRef.current.autoPan?.enabled(id) ?? false
    const tick = async () => {
      frame = null
      const { autoPan, toFlow } = optionsRef.current
      const bounds = autoPan?.getBounds()
      if (!autoPan || !bounds || finished) return
      const delta = autoPanDelta(
        { x: lastClient.x - bounds.left, y: lastClient.y - bounds.top },
        bounds,
        autoPan.speed(),
      )
      if (delta.x !== 0 || delta.y !== 0) {
        if (await autoPan.panBy(delta)) preview(toFlow(lastClient))
      }
      if (!finished) frame = requestAnimationFrame(() => { void tick() })
    }

    const handleMove = (e: PointerEvent) => {
      travelled = true
      keepOrigin = e.metaKey || e.ctrlKey
      lastClient.x = e.clientX
      lastClient.y = e.clientY
      preview(optionsRef.current.toFlow(lastClient))
      if (panning && frame === null) frame = requestAnimationFrame(() => { void tick() })
    }

    const finish = (commit: boolean) => {
      finished = true
      stopResting()
      if (frame !== null) cancelAnimationFrame(frame)
      document.documentElement.classList.remove(SWAPPING_CLASS)
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleCancel)
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleModifier, true)

      // Was unter dem Zeiger liegt, gilt: auch ein Rand, dessen Ruhe noch lief.
      const final = commit ? hit : null
      if (!final) {
        // **Verschieben**: Ohne Treffer bleibt der Zettel liegen, wo er ist —
        // eingerastet, der Ursprung bleibt ein Loch, niemand rückt nach.
        // Abgebrochen wird nur noch mit Escape (CONTEXT.md, Tauschpunkt).
        if (commit && landing) optionsRef.current.onCommit([{ id, ...landing }])
        else optionsRef.current.onCancel()
        return
      }

      /*
        Daheim: Der eigene Platz ist ein Ziel, aber ein folgenloses — der
        Zettel liegt schon dort, und niemand hat Platz zu machen. Nichts
        schreiben heißt hier abbrechen: Die Vorschau geht zurück auf die
        Ausgangslage, und ⌘Z bekommt keinen Schritt, der nichts tat.
      */
      if (final.kind === 'home') {
        optionsRef.current.onCancel()
        return
      }

      /*
        Leerplatz: hinlegen — erst jetzt rastet der Getragene ein (ein
        Einrasten schon während des Ziehens war zu hart). Am ZIEL rückt
        niemand, dort ist ja frei; seine alte Reihe schließt sich aber hinter
        ihm, wie beim Einreihen (⌘/Strg hält sie offen). Sonst verhielte sich
        dieselbe Geste am ENDE einer Reihe anders als in ihrer Mitte: Dort
        gibt es keinen Rand mehr, angehängt wird im Feld dahinter — und genau
        dieser Weg ließ die Herkunftsspalte offen (gemeldet 2026-09-04).
      */
      if (final.kind === 'slot') {
        const plan = keepOrigin ? null : getCloseOriginPlan(start, id, final.rect)
        const rect = plan?.rect ?? final.rect
        optionsRef.current.onCommit([{ id, x: rect.x, y: rect.y }, ...(plan?.moves ?? [])])
        return
      }

      // *davor* / *dahinter*: einreihen — die Reihe öffnet, der Ursprung
      // schließt (außer ⌘/Strg ist unten).
      const shove = shoveTargetOf(final)
      if (shove) {
        const plan = getShovePlan(start, id, shove, !keepOrigin)
        optionsRef.current.onCommit([{ id, x: plan.rect.x, y: plan.rect.y }, ...plan.moves])
        return
      }

      // *auf*: tauschen.
      const swapped = swapPositions(dragged.rect, final.owner.rect)
      optionsRef.current.onCommit([
        { id, ...swapped.a },
        { id: final.owner.id, ...swapped.b },
      ])
    }

    const handleUp = (e: PointerEvent) => {
      keepOrigin = e.metaKey || e.ctrlKey
      finish(true)
    }
    const handleCancel = () => finish(false)

    /*
      Der Modifikator wechselt auch ohne Mausbewegung — sonst stünde das Bild
      falsch, bis die Hand sich rührt. `Meta` und `Control`, beide Seiten.
    */
    const handleModifier = (e: KeyboardEvent) => {
      const next = e.metaKey || e.ctrlKey
      if (next === keepOrigin) return
      keepOrigin = next
      preview(lastPointer)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      handleModifier(e)
      if (e.key !== 'Escape') return
      // Solange die Geste läuft, gehört Escape ihr. Ohne `stopPropagation`
      // liefe `useCanvasEscape` mit und hübe die Auswahl auf — der Overlay
      // verschwände mitten im Abbruch.
      e.preventDefault()
      e.stopPropagation()
      finish(false)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleCancel)
    // In der Erfassungsphase, damit wir VOR dem Listener am Canvas-Element
    // drankommen.
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleModifier, true)

    // Einmal sofort, damit der gehaltene Zustand steht, bevor sich etwas rührt.
    preview(origin)
  }, [])

  return { startSwap }
}
